import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from 'hooks/useApi';
import styles from "./Blog.module.css";

const Blog = () => {
  const navigate = useNavigate();
  
  const [blogs, setBlogs] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState(new Set()); // Track loaded pages
  
  const PAGE_SIZE = 6;
  const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

  const apiOptions = useMemo(() => ({
    params: {
      page: currentPage,
      page_size: PAGE_SIZE
    }
  }), [currentPage]);

  const { data: blogData, loading, error } = useApi('/blog', apiOptions);

  // Transform and accumulate blog data
  useEffect(() => {
    if (blogData?.results) {
      // Prevent duplicate processing of the same page
      if (loadedPages.has(currentPage)) {
        return;
      }

      const transformedBlogs = blogData.results.map((blog) => ({
        id: blog.id,
        date: new Date(blog.date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        title: blog.name,
        description: blog.sub_heading,
        image: `${CMS_BASE_URL}/attachments/${blog.thumbnail}`,
        slug: blog.slug
      }));

      if (currentPage === 1) {
        // Reset blogs for first page
        setBlogs(transformedBlogs);
        setLoadedPages(new Set([1]));
      } else {
        // Append to existing blogs for subsequent pages
        setBlogs(prev => {
          // Additional safety check - ensure no duplicates by ID
          const existingIds = new Set(prev.map(blog => blog.id));
          const newBlogs = transformedBlogs.filter(blog => !existingIds.has(blog.id));
          return [...prev, ...newBlogs];
        });
        setLoadedPages(prev => new Set([...prev, currentPage]));
      }

      setHasMore(blogData.next_page !== null);
    }
  }, [blogData, currentPage, CMS_BASE_URL, loadedPages]);

  // Load more blogs (increment page)
  const loadMoreBlogs = useCallback(() => {
    if (hasMore && !loading && !loadedPages.has(currentPage + 1)) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, loading, loadedPages, currentPage]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        >= document.documentElement.offsetHeight - 1000
        && hasMore && !loading
      ) {
        loadMoreBlogs();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreBlogs, hasMore, loading]);

  // Reset to first page
  const resetBlogs = useCallback(() => {
    setCurrentPage(1);
    setBlogs([]);
    setHasMore(true);
    setLoadedPages(new Set());
  }, []);

  const handleBlogClick = (blog) => {
    navigate(`/blog/${blog.slug}`);
  };

  const handleImageError = (e) => {
    e.target.src = '/Assets/Images/Cases/drone.svg';
  };

  // Debug logging (remove in production)
  console.log('Debug Info:', {
    currentPage,
    blogsCount: blogs.length,
    apiResultsCount: blogData?.results?.length || 0,
    loadedPages: Array.from(loadedPages),
    hasMore,
    loading
  });

  if (error && blogs.length === 0) {
    return (
      <div className={styles.blogSection}>
        <h2 className={styles.blogTitle}>Blog</h2>
        <div className={styles.errorMessage}>
          <p>Error loading blogs: {error}</p>
          <button onClick={resetBlogs} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.blogSection}>
      <h2 className={styles.blogTitle}>Blog</h2>
      
      {blogs.length === 0 && loading ? (
        <div className={styles.loadingMessage}>
          <p>Loading blogs...</p>
        </div>
      ) : (
        <>
          <div className={styles.blogGrid}>
            {blogs.map((blog) => (
              <div 
                key={blog.id} 
                className={styles.blogCard}
                onClick={() => handleBlogClick(blog)}
              >
                <img 
                  src={blog.image} 
                  alt={blog.title} 
                  className={styles.blogImage}
                  onError={handleImageError}
                />
                <div className={styles.blogContent}>
                  <p className={styles.blogDate}>{blog.date}</p>
                  <h3 className={styles.blogHeading}>{blog.title}</h3>
                  <p className={styles.blogDescription}>{blog.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {loading && blogs.length > 0 && (
            <div className={styles.loadingMore}>
              <p>Loading more blogs...</p>
            </div>
          )}
          
          {blogs.length === 0 && !loading && (
            <div className={styles.noBlogs}>
              <p>No blogs found</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Blog;